import * as React from 'react';
import { isNil, isArray } from 'lodash/fp';
import { isEqual, isFunction, isString } from 'lodash';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  PaperProps,
  PopperProps,
  Popper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete, AutocompleteClassKey } from '@material-ui/lab';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { classes } from 'typestyle';
import { Check, Close, KeyboardArrowDown } from '@material-ui/icons';

import styles, {
  baseAutocompleteStyles,
  customPopperStyle,
  generalPopper,
  generalPopperHeight,
  actionButtons,
  multiSelectOption,
} from './InputSuggest.styles';
import VirtualizedListbox, {
  type VirtualizedListboxProps,
} from 'src/common-ui/components/VirtualizedListbox/VirtualizedListbox';
import {
  AutocompleteCloseReason,
  AutocompleteInputChangeReason,
} from '@material-ui/lab/useAutocomplete/useAutocomplete';

const selectAll: Suggestion = { label: 'Select All', value: '$selectAll' };
const deselectAll: Suggestion = { label: 'Deselect All', value: '$selectAll' };

/** Checks for null, undefined, or empty string. isNil doesnt catch empty string & isEmpty is true for numbers */
function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

const ErrorTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: 'rgba(247, 92, 105, 0.97)',
    color: '#FFF',
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}))(Tooltip);

export const ErrorIcon = (props: Suggestion) => {
  return props.icon ? (
    <ErrorTooltip title="Conflict: Current value not found in valid values.">
      <i className={classes(styles.errorIcon, props.icon)} />
    </ErrorTooltip>
  ) : null;
};

interface ListboxVirtualizedProps extends VirtualizedListboxProps {}

export const ListboxVirtualized = React.forwardRef<HTMLUListElement, ListboxVirtualizedProps>(
  function ListboxVirtualized(listboxProps, ref) {
    const { children, selectedCount = 0, totalCount = 0, onToggleAll, ...rest } = listboxProps;

    return !rest.isMultiSelect ? (
      <VirtualizedListbox style={{ padding: 0 }} ref={ref} {...rest}>
        {children}
      </VirtualizedListbox>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 'inherit' }}>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: '1px 3px',
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Selected: {selectedCount} / {totalCount}
          </Typography>
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Checkbox
                name="selectAll"
                size="small"
                style={{ padding: '0 5px' }}
                checked={selectedCount === totalCount}
                onChange={(_evt, checked) => {
                  if (onToggleAll) {
                    if (checked) {
                      onToggleAll([selectAll]);
                    } else {
                      onToggleAll([deselectAll]);
                    }
                  }
                }}
              />
            }
            label={
              <Typography variant="caption" color="textSecondary">
                Select All
              </Typography>
            }
            labelPlacement="start"
          />
        </Box>

        <Divider variant="fullWidth" />

        <VirtualizedListbox style={{ padding: 0 }} ref={ref} {...rest}>
          {children}
        </VirtualizedListbox>
      </div>
    );
  },
);

interface PopperWidthOverrideProps extends PopperProps {
  popperWidth?: number;
}

const PopperWidthOverride = function ({ popperWidth, ...restProps }: PopperWidthOverrideProps) {
  const { anchorEl, style } = restProps;
  let newWidth = popperWidth ?? style?.width;

  if (!popperWidth && anchorEl != null) {
    const el = isFunction(anchorEl) ? anchorEl() : anchorEl;
    newWidth = el.clientWidth;
  }

  return <Popper {...restProps} placement="bottom-start" style={{ ...style, width: newWidth, minWidth: newWidth }} />;
};

export interface CustomAutocompleteClasses {
  /** Overrides passed directly to the Autocomplete component's `classes` property  */
  autocomplete?: Partial<Record<AutocompleteClassKey, string>>;
}

export interface MultiselectInputSuggestProps {
  multiSelect?: boolean;
}

interface InputSuggestProps extends MultiselectInputSuggestProps {
  classes?: CustomAutocompleteClasses;
  validValues: Suggestion[];
  selected: Suggestion[] | Suggestion | string | undefined;
  valid?: boolean;
  placeholder?: string;
  onSelect?: (sugg: Suggestion) => void;
  onMultiSelect?: (suggs: Suggestion[]) => void;
  dataQa?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  defaultOpenOnFocus?: boolean;
  /** **For lazy loading** */
  open?: boolean;
  onOpen?: () => void;
  onClose?: (event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => void;
  allowPortal?: boolean;
  /** ability to specify a wider width for the dropdown */
  popperWidth?: number;
  onCancel?: () => void;
  onSubmit?: () => void;
}

export interface Suggestion {
  label: string;
  value: string | number | object;
  icon?: string;
}
/** Manages the search value in dropdown */
interface InputSuggestState {
  inputValue: string;
}

export default class InputSuggest extends React.Component<InputSuggestProps> {
  static defaultProps = {
    multiSelect: false,
  };

  state: InputSuggestState = {
    inputValue: '',
  };

  private BoxRef = React.createRef<HTMLDivElement>();
  private inputRef = React.createRef<HTMLInputElement>();
  lastHighlightedIndex: number = -1;
  lastHighlightedOption: Suggestion | null = null;

  componentDidUpdate(prevProps: InputSuggestProps) {
    // don't want to trigger when:
    // - initial setup, from undefined -> boolean value
    // - prev isLoading matches current
    // - currently loading
    const justFinishedLoading =
      !isNil(prevProps.isLoading) && !isEqual(prevProps.isLoading, this.props.isLoading) && !this.props.isLoading;
    const selectedValueChanged = !isEqual(prevProps.selected, this.props.selected);

    if (justFinishedLoading || selectedValueChanged) {
      this.focusAndSelectInput();
    }
  }

  /**
   * Highlights the input text so the user can just start typing to search.
   * For single-select, MUI fills in the selected label a bit later (since the createRoot switch - react 18),
   * and filling it in clears any highlight - so check each frame until the label is there, then highlight it.
   * Stops after a few tries so it can't loop forever (like if the user has already started typing)
   */
  focusAndSelectInput = (attemptsLeft: number = 10) => {
    requestAnimationFrame(() => {
      const input = this.inputRef.current;
      if (!input) return;

      const labelNotFilledInYet = !this.props.multiSelect && input.value !== (this.getSelection()[0]?.label ?? '');
      if (labelNotFilledInYet) {
        if (attemptsLeft > 0) this.focusAndSelectInput(attemptsLeft - 1);
        return;
      }

      input.focus();
      input.select();
    });
  };

  getSelection() {
    const { selected, validValues } = this.props;
    if (isNil(selected)) return [];
    if (isString(selected)) {
      const isValidSelection = validValues.find((vv) => selected === vv.value || selected === vv.label);
      return [
        {
          label: selected,
          value: selected,
          icon: isValidSelection ? undefined : 'fal fa-exclamation-triangle',
        },
      ];
    }
    if (!isArray(selected) && selected.value === '') {
      return [];
    }
    return isArray(selected) ? selected : [selected];
  }

  handleSingleSelect = (selection: Suggestion | null) => {
    const { onSelect } = this.props;
    if (selection && onSelect) {
      onSelect(selection);
    }
  };

  handleMultiSelect = (selections: Suggestion[]) => {
    const { onMultiSelect, validValues } = this.props;
    let finalSelections: Suggestion[];

    if (onMultiSelect) {
      if (selections.indexOf(selectAll) >= 0) {
        // select all
        finalSelections = validValues;
      } else if (selections.indexOf(deselectAll) >= 0) {
        // deselectAll
        finalSelections = [];
      } else {
        // normal multi selection
        finalSelections = selections;
      }

      onMultiSelect(finalSelections);
    }
  };

  // only wired up for multiSelect (see `Autocomplete`'s `onInputChange` below) -
  // mui by default resets search to '' when a user makes a selection, block that so it stays
  handleInputChange = (_event: React.ChangeEvent<{}>, newInputValue: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'reset') {
      return;
    }
    this.setState({ inputValue: newInputValue });
  };

  showActionButtons() {
    const { multiSelect, onCancel, onSubmit } = this.props;
    return multiSelect && (!isNil(onCancel) || !isNil(onSubmit));
  }

  // Popper/Paper must keep a stable identity across renders. Inline components get a new type every render,
  // which remounts the popup (and the virtualized list inside it), resetting scroll to the top after each selection
  PopperComponent = (props: PopperProps) => {
    return (
      <PopperWidthOverride
        {...props}
        data-qa="input-suggest-popper"
        anchorEl={this.BoxRef.current}
        popperWidth={this.props.popperWidth}
      />
    );
  };

  PaperComponent = (props: PaperProps) => {
    const { onCancel, onSubmit } = this.props;
    return (
      <Paper
        {...props}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
      >
        {props.children}
        {this.showActionButtons() && (
          <div className={actionButtons}>
            <IconButton aria-label="Submit selection" data-qa="submit-selection" onClick={onSubmit} size="small">
              <Check />
            </IconButton>
            <IconButton aria-label="Cancel selection" data-qa="cancel-selection" onClick={onCancel} size="small">
              <Close />
            </IconButton>
          </div>
        )}
      </Paper>
    );
  };

  render() {
    const {
      validValues,
      multiSelect,
      placeholder,
      dataQa,
      disabled,
      isLoading,
      onBlur,
      open,
      onOpen,
      onClose,
      defaultOpenOnFocus,
      allowPortal = false,
      classes: overrides,
    } = this.props;

    const selected = this.getSelection();

    // Only auto-highlight the first option when it's not multiSelect AND the selected option has empty value
    const shouldAutoHighlight = !multiSelect && (selected.length === 0 || selected[0]?.value === '');

    // Find the index of the selected option in the validValues array
    const selectedIndex =
      !multiSelect && selected.length > 0 && selected[0]?.value !== ''
        ? validValues.findIndex((option) => option.value === selected[0].value)
        : -1;
    const highlightedIndex = multiSelect ? this.lastHighlightedIndex : shouldAutoHighlight ? 0 : selectedIndex;
    const options = validValues;
    const showActionButtons = this.showActionButtons();

    const base = baseAutocompleteStyles;
    // apply base and override styles cleanly if both exist
    const mergedClasses = {
      ...base,
      option: multiSelect ? multiSelectOption : base.option,
      root: classes(base.root, overrides?.autocomplete?.root),
      inputRoot: classes(base.inputRoot, overrides?.autocomplete?.inputRoot),
      input: classes(base.input, overrides?.autocomplete?.input),
      // `ag-custom-component-popup` allows custom popups from editors (dropdown options) to properly notify ag-grid when to stop editing
      popper: classes(
        generalPopper,
        allowPortal ? 'ag-custom-component-popup' : customPopperStyle,
        overrides?.autocomplete?.popper,
      ),
    };

    let openOnFocus = false;
    if (!multiSelect) {
      openOnFocus = defaultOpenOnFocus ?? false;
    } else {
      // for multiSelects that are headitors, we want to take the default if supplied
      openOnFocus = defaultOpenOnFocus ?? true;
    }

    return (
      <FormGroup className={styles.container} data-qa="form-group-container">
        <div className={classes('select-container', disabled ? 'disabled' : '')} ref={this.BoxRef}>
          <Box style={{ backgroundColor: 'white' }} className="input-suggest-container" data-qa={dataQa}>
            <Autocomplete
              forcePopupIcon
              selectOnFocus
              disablePortal={!allowPortal}
              disableCloseOnSelect={multiSelect || showActionButtons}
              disableClearable={multiSelect}
              openOnFocus={openOnFocus}
              multiple={multiSelect}
              // only controlled for multiSelect, to preserve search across selections; single-select never had this problem
              inputValue={multiSelect ? this.state.inputValue : undefined}
              onInputChange={multiSelect ? this.handleInputChange : undefined}
              disabled={disabled}
              loading={isLoading}
              options={options}
              open={open}
              PopperComponent={this.PopperComponent}
              PaperComponent={this.PaperComponent}
              onOpen={onOpen}
              onClose={onClose}
              popupIcon={<KeyboardArrowDown />}
              classes={mergedClasses}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              value={multiSelect ? selected : selected[0] || null}
              onChange={(_evt, newValue) => {
                if (!multiSelect) {
                  // Map MUI "clear" (null) to an null Suggestion so it propagates properly upwards
                  const next: Suggestion = (newValue as Suggestion) ?? { label: null, value: null };
                  this.handleSingleSelect(next);
                } else {
                  this.handleMultiSelect((newValue as Suggestion[]) || []);
                }
              }}
              loadingText="Loading..."
              renderOption={(option, { selected: isSelected }) => (
                <div
                  data-qa="option-value"
                  style={{
                    ...(isEmptyValue(option.value) ? { color: '#9e9e9e', fontStyle: 'italic' } : {}),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    ...(multiSelect ? { width: '100%', gap: '8px' } : {}),
                  }}
                >
                  {(isEmptyValue(option.value) ? '(empty)' : option.label || option.value) as React.ReactNode}
                  {multiSelect && (
                    <Check fontSize="small" style={{ flexShrink: 0, visibility: isSelected ? 'visible' : 'hidden' }} />
                  )}
                </div>
              )}
              renderTags={() => null}
              // Track MUI's internal highlighted option.
              // Without this, Autocomplete may reset the highlight to the first option
              // after selecting, which triggers a scroll-to-top in the virtualized list.
              onHighlightChange={(_e, option) => {
                this.lastHighlightedOption = (option as Suggestion) ?? null;
                this.lastHighlightedIndex = option
                  ? this.props.validValues.findIndex((v) => v.value === (option as Suggestion).value)
                  : -1;
              }}
              ListboxComponent={ListboxVirtualized as unknown as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
              ListboxProps={{
                maxListHeight: generalPopperHeight,
                autoHighlight: shouldAutoHighlight,
                highlightedIndex: highlightedIndex,
                isMultiSelect: !!multiSelect,
                selectedCount: selected.length,
                totalCount: options.length,
                onToggleAll: this.handleMultiSelect,
                key: multiSelect ? 'multiselect-list' : 'singleselect-list',
              }}
              renderInput={(params) => {
                // only render the start adornment when an error icon is setup
                const selectedItem = selected.length > 0 ? selected[0] : null;
                const startAdornment =
                  selectedItem && selectedItem.icon !== undefined ? <ErrorIcon {...selectedItem} /> : null;

                const endAdornmentChildren = React.Children.toArray(
                  React.isValidElement(params.InputProps.endAdornment)
                    ? params.InputProps.endAdornment.props.children
                    : [],
                );

                return (
                  <TextField
                    {...params}
                    placeholder={isLoading ? '' : (placeholder ?? 'Search...')}
                    variant="outlined"
                    style={{
                      backgroundColor: disabled ? 'rgb(242, 242, 242)' : undefined,
                    }}
                    onBlur={onBlur}
                    autoFocus={true}
                    value={multiSelect ? selected : selected[0] || null}
                    data-qa="selected-option"
                    inputRef={this.inputRef}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment,
                      endAdornment: (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            pr: 1,
                          }}
                        >
                          {endAdornmentChildren.length > 0 &&
                            endAdornmentChildren.map((child, index) => (
                              <React.Fragment key={index}>
                                {((endAdornmentChildren.length === 1 && index === 0) ||
                                  (endAdornmentChildren.length > 1 && index > 0)) && (
                                  <Box
                                    width="1px"
                                    height={multiSelect ? '85%' : '50%'}
                                    mx={0.5}
                                    bgcolor="rgba(98, 95, 95, 0.2)"
                                    alignSelf="center"
                                  />
                                )}
                                {child}
                              </React.Fragment>
                            ))}
                        </Box>
                      ),
                    }}
                  />
                );
              }}
            />
          </Box>
        </div>
      </FormGroup>
    );
  }
}
